import { z } from "zod";

// The Editor's browser↔BFF shapes: the scope every verb's server function
// validates and the reorder target (#797), and each verb's own input (#799's
// group verbs and header write, #800's line verbs, #801's selection verbs,
// #802's location verbs and #803's per-plan verbs below; #804 adds its). The
// internal wire's faces live on their declarations
// (`@/server/data/internal-api/spec-editor`), never
// here ("Two seams, two homes for a schema"); the result's client-graph type is
// the cache updater's (`@/hooks/spec-editor-cache`). Each shape here is one the
// wire's flat scope accepts as it is, so a verb's server function hands it on
// unchanged.

const cursor = z.number().int().min(0);
const recordId = z.number().int().min(1);

/** The three statuses a line holds — the wire's own set, pinned by the
 *  spec-editor drift test. */
export const SPEC_LINE_STATUS_NAMES = ["needs_attention", "in_progress", "finalized"] as const;

export type SpecLineStatusName = (typeof SPEC_LINE_STATUS_NAMES)[number];

/**
 * The statuses the Review & Publish dialog's step 1 lists (#950): every line
 * not finalized — In Progress beside Needs Attention, which the source's
 * single-valued filter dropped. The one list the review read asks for and the
 * review scope names, so the list the dialog shows and the list a result
 * corrects can never disagree.
 */
export const REVIEW_LINE_STATUSES = [
  "in_progress",
  "needs_attention",
] as const satisfies readonly SpecLineStatusName[];

/** Line statuses as one list: at least one, each a line status. */
export const lineStatusesSchema = z.array(z.enum(SPEC_LINE_STATUS_NAMES)).min(1);

/**
 * The caller's view, sent with every verb (ADR-0014 decision 4 as amended):
 * the base view, a search term as typed (no trim — spaces are a term), or the
 * Review & Publish dialog's step 1, which names the line statuses it lists
 * (#950) as a search names its term. A search names its term: with the box empty the
 * caller's view is the base view, and it sends that. `cursor` is the
 * named scope's offset cursor — the one its last loaded page answered with;
 * `baseCursor` the base view's, or `null` when the caller has none loaded.
 * `snapshotGroupIds` is a search view's groups in its order — the snapshot
 * the server decides the edit from (#852/D2, #910) — left out when the view
 * is not cached; the one scope builder (`@/hooks/spec-editor-target`) fills it.
 */
export const editorScopeSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("base"), cursor }),
  z.strictObject({
    kind: z.literal("search"),
    term: z.string().min(1),
    cursor,
    baseCursor: cursor.nullable(),
    snapshotGroupIds: z.array(recordId).optional(),
  }),
  z.strictObject({
    kind: z.literal("review"),
    cursor,
    baseCursor: cursor.nullable(),
    statuses: lineStatusesSchema,
  }),
]);

export type EditorScope = z.infer<typeof editorScopeSchema>;

/** A reorder's target: the sibling the moved row lands after, `null` for first. */
export const afterIdSchema = z.number().int().nullable();

export type AfterId = z.infer<typeof afterIdSchema>;

// ---------------------------------------------------------------------------
// The group verbs and the header write (#799)
// ---------------------------------------------------------------------------
//
// Each verb's server function validates with one of these. Every one names
// the spec and version it edits and the caller's view — the shape the cache
// updater keys on (`EditorCacheTarget` in `@/hooks/spec-editor-cache`), so a
// hook's variables are its cache target as they are. The name, Community ID
// and status rules are Python's: a refusal there is a tagged error the dialog
// can show, where a rejection here would be scrubbed to a generic message. The
// status rides as the three-value enum all the same (`SPEC_LINE_STATUS_NAMES`,
// above) — a closed set the menu offers, which the wire's own `Literal`
// refuses anyway.

const editorTarget = {
  specificationId: recordId,
  versionId: recordId,
  scope: editorScopeSchema,
};

const groupTarget = { ...editorTarget, groupId: recordId };

/** Add group: the name as typed, the note as sent — `""` for none. */
export const specGroupCreateInputSchema = z.strictObject({
  ...editorTarget,
  name: z.string(),
  notes: z.string(),
});

/** Edit group / note: both, together; `""` clears the note (`Clear Note`). */
export const specGroupUpdateInputSchema = z.strictObject({
  ...groupTarget,
  name: z.string(),
  notes: z.string(),
});

export const specGroupDeleteInputSchema = z.strictObject(groupTarget);

/** Mark group as `<status>`. */
export const specGroupStatusInputSchema = z.strictObject({
  ...groupTarget,
  status: z.enum(SPEC_LINE_STATUS_NAMES),
});

/** Move group: the group it lands after, `null` for first. */
export const specGroupMoveInputSchema = z.strictObject({
  ...groupTarget,
  afterId: afterIdSchema,
});

/**
 * The header pencil's write: the spec's name, its Community ID (a community
 * spec's dialog sends it; absent leaves it) and its notes — always sent, as the
 * source's header always sent them, trimmed by the dialog (#806).
 */
export const specSummaryEditInputSchema = z.strictObject({
  ...editorTarget,
  name: z.string(),
  communityId: z.string().optional(),
  notes: z.string(),
});

/** Mark all as finalized (#950): the target alone — the verb only finalizes. */
export const specVersionFinalizeAllInputSchema = z.strictObject(editorTarget);

export type SpecGroupCreateBrowserInput = z.infer<typeof specGroupCreateInputSchema>;
export type SpecGroupUpdateBrowserInput = z.infer<typeof specGroupUpdateInputSchema>;
export type SpecGroupDeleteBrowserInput = z.infer<typeof specGroupDeleteInputSchema>;
export type SpecGroupStatusBrowserInput = z.infer<typeof specGroupStatusInputSchema>;
export type SpecGroupMoveBrowserInput = z.infer<typeof specGroupMoveInputSchema>;
export type SpecSummaryEditBrowserInput = z.infer<typeof specSummaryEditInputSchema>;
export type SpecVersionFinalizeAllBrowserInput = z.infer<typeof specVersionFinalizeAllInputSchema>;

// ---------------------------------------------------------------------------
// The line verbs (#800)
// ---------------------------------------------------------------------------
//
// Add specification line names the group it appends to; every other line verb
// names the line. The name rule and the status re-check are Python's, as the
// group verbs' are.

const lineTarget = { ...editorTarget, lineId: recordId };

/** Add specification line: the name as typed. */
export const specLineCreateInputSchema = z.strictObject({
  ...groupTarget,
  name: z.string(),
});

/**
 * Edit specification line: name and note together (`""` clears the note,
 * `Clear Note`), and the group the line is in after the edit — the dialog's
 * select, its own group or another of the version.
 */
export const specLineUpdateInputSchema = z.strictObject({
  ...lineTarget,
  name: z.string(),
  notes: z.string(),
  groupId: recordId,
});

/** The pill and the lock (`in_progress`): one of the three statuses. */
export const specLineStatusInputSchema = z.strictObject({
  ...lineTarget,
  status: z.enum(SPEC_LINE_STATUS_NAMES),
});

export const specLineDeleteInputSchema = z.strictObject(lineTarget);

/** Move line: the line of the same group it lands after, `null` for first. */
export const specLineMoveInputSchema = z.strictObject({
  ...lineTarget,
  afterId: afterIdSchema,
});

/** Duplicate line: the copy's name, as the user confirmed it. */
export const specLineDuplicateInputSchema = z.strictObject({
  ...lineTarget,
  name: z.string(),
});

export type SpecLineCreateBrowserInput = z.infer<typeof specLineCreateInputSchema>;
export type SpecLineUpdateBrowserInput = z.infer<typeof specLineUpdateInputSchema>;
export type SpecLineStatusBrowserInput = z.infer<typeof specLineStatusInputSchema>;
export type SpecLineDeleteBrowserInput = z.infer<typeof specLineDeleteInputSchema>;
export type SpecLineMoveBrowserInput = z.infer<typeof specLineMoveInputSchema>;
export type SpecLineDuplicateBrowserInput = z.infer<typeof specLineDuplicateInputSchema>;

// ---------------------------------------------------------------------------
// The selection verbs (#801)
// ---------------------------------------------------------------------------
//
// Add Selections names the line it adds to; every other selection verb names
// the selection and its line — the write's scope (the selections rulings R1).
// The line clear names the line alone. The name rule is Python's.

const selectionTarget = { ...lineTarget, selectionId: recordId };

/** Add Selections: no name — the menu sends none, and the server names it
 *  `Selection N` — or a name as typed. */
export const specSelectionCreateInputSchema = z.strictObject({
  ...lineTarget,
  name: z.string().optional(),
});

/** Edit Selection: name and note together (`""` clears the note, `Clear Note`). */
export const specSelectionUpdateInputSchema = z.strictObject({
  ...selectionTarget,
  name: z.string(),
  notes: z.string(),
});

export const specSelectionDeleteInputSchema = z.strictObject(selectionTarget);

/** Select: the item id alone — the server writes the category from the item. */
export const specSelectionItemInputSchema = z.strictObject({
  ...selectionTarget,
  itemId: recordId,
});

/** Clear (the menu) and Remove Selected (the card and the dialog): one verb. */
export const specSelectionClearItemInputSchema = z.strictObject(selectionTarget);

/** Set as Paid Upgrade: the selection's own flag. */
export const specSelectionPaidUpgradeInputSchema = z.strictObject({
  ...selectionTarget,
  isPaidUpgrade: z.boolean(),
});

/** Clear on a line: the item off every selection of the line. */
export const specLineClearInputSchema = z.strictObject(lineTarget);

export type SpecSelectionCreateBrowserInput = z.infer<typeof specSelectionCreateInputSchema>;
export type SpecSelectionUpdateBrowserInput = z.infer<typeof specSelectionUpdateInputSchema>;
export type SpecSelectionDeleteBrowserInput = z.infer<typeof specSelectionDeleteInputSchema>;
export type SpecSelectionItemBrowserInput = z.infer<typeof specSelectionItemInputSchema>;
export type SpecSelectionClearItemBrowserInput = z.infer<typeof specSelectionClearItemInputSchema>;
export type SpecSelectionPaidUpgradeBrowserInput = z.infer<
  typeof specSelectionPaidUpgradeInputSchema
>;
export type SpecLineClearBrowserInput = z.infer<typeof specLineClearInputSchema>;

// ---------------------------------------------------------------------------
// The Resets (#1084)
// ---------------------------------------------------------------------------
//
// Each names its row alone: the server finds the base row by lineage.

/** Reset to Base / to Core on a line. */
export const specLineResetInputSchema = z.strictObject(lineTarget);

/** Reset to Base / to Core on a selection row. */
export const specSelectionResetInputSchema = z.strictObject(selectionTarget);

/** Reset group name and notes to Base / to Core. */
export const specGroupResetInputSchema = z.strictObject(groupTarget);

export type SpecLineResetBrowserInput = z.infer<typeof specLineResetInputSchema>;
export type SpecSelectionResetBrowserInput = z.infer<typeof specSelectionResetInputSchema>;
export type SpecGroupResetBrowserInput = z.infer<typeof specGroupResetInputSchema>;

// ---------------------------------------------------------------------------
// The location verbs (#802)
// ---------------------------------------------------------------------------
//
// The checklist, Add Location, the type radio and a room's note name the line;
// a room's note also names its placement. A room's rename and delete name the
// room under the version — a room is the version's, not a line's. The name
// rule is Python's (not blank once trimmed, a collision linked or refused).

/** A line's three location types — the wire's own set
 *  (`SPEC_LINE_LOCATION_TYPES`). */
export const SPEC_LINE_LOCATION_TYPE_NAMES = ["applicable", "per_plan", "specific"] as const;

export type SpecLineLocationTypeName = (typeof SPEC_LINE_LOCATION_TYPE_NAMES)[number];

const locationTarget = { ...editorTarget, locationId: recordId };

/** The checklist: the ids of the rooms the line should have, by id. */
export const specLineLocationsInputSchema = z.strictObject({
  ...lineTarget,
  locationIds: z.array(recordId),
});

/** Add Location: the name as typed. */
export const specLocationCreateInputSchema = z.strictObject({
  ...lineTarget,
  name: z.string(),
});

/** Rename a room: the name as typed. */
export const specLocationRenameInputSchema = z.strictObject({
  ...locationTarget,
  name: z.string(),
});

export const specLocationDeleteInputSchema = z.strictObject(locationTarget);

/** A room's note on the line, rich text (`""` is `Clear Note`). */
export const specLineLocationNotesInputSchema = z.strictObject({
  ...lineTarget,
  lineLocationId: recordId,
  notes: z.string(),
});

/** The location-type radio: one of the three. */
export const specLineLocationTypeInputSchema = z.strictObject({
  ...lineTarget,
  locationType: z.enum(SPEC_LINE_LOCATION_TYPE_NAMES),
});

export type SpecLineLocationsBrowserInput = z.infer<typeof specLineLocationsInputSchema>;
export type SpecLocationCreateBrowserInput = z.infer<typeof specLocationCreateInputSchema>;
export type SpecLocationRenameBrowserInput = z.infer<typeof specLocationRenameInputSchema>;
export type SpecLocationDeleteBrowserInput = z.infer<typeof specLocationDeleteInputSchema>;
export type SpecLineLocationNotesBrowserInput = z.infer<typeof specLineLocationNotesInputSchema>;
export type SpecLineLocationTypeBrowserInput = z.infer<typeof specLineLocationTypeInputSchema>;

// ---------------------------------------------------------------------------
// The per-plan verbs (#803)
// ---------------------------------------------------------------------------
//
// Set as Per Plan names the line; the manufacturer names the row's own
// selection under it. Both carry the value to set — the menu's checkbox
// computes it, the verb never toggles.

/** Set as Per Plan: the line's own flag. */
export const specLinePerPlanInputSchema = z.strictObject({
  ...lineTarget,
  isPerPlan: z.boolean(),
});

/** The per-plan selection's manufacturer: an id, or `null` to clear it. */
export const specSelectionManufacturerInputSchema = z.strictObject({
  ...selectionTarget,
  manufacturerId: recordId.nullable(),
});

export type SpecLinePerPlanBrowserInput = z.infer<typeof specLinePerPlanInputSchema>;
export type SpecSelectionManufacturerBrowserInput = z.infer<
  typeof specSelectionManufacturerInputSchema
>;
